export interface FaqItem {
  id: string;
  title: string;
  description: string;
}

export interface FaqSection {
  id: string;
  title: string;
  items: FaqItem[];
}

export const supportData: FaqSection[] = [
  {
    id: 'getting-started',
    title: 'Getting started',
    items: [
      {
        id: 'getting-started-account',
        title: 'Do I need an account?',
        description:
          'You can browse event details and auction items without one. To buy tickets, place a bid, buy raffle tickets, or make a donation, select Login and sign in or create a free account. It takes about a minute.',
      },
      {
        id: 'getting-started-app',
        title: 'Do I need to download an app?',
        description:
          "No. Everything works in the web browser on your phone, tablet, or computer. Bookmark the event page so it's easy to find on event night.",
      },
      {
        id: 'getting-started-password',
        title: 'I forgot my password. What do I do?',
        description: "Select Login, then Forgot password? Then enter your email. We'll send you a link to reset it.",
      },
      {
        id: 'getting-started-age',
        title: 'Is there an age requirement?',
        description:
          'You must be 18 or older to bid in the auction. The organization may set a higher age for some activities, such as the raffle.',
      },
    ],
  },
  {
    id: 'tickets',
    title: 'Tickets',
    items: [
      {
        id: 'tickets-buy',
        title: 'How do I buy tickets?',
        description: 'Select Buy Tickets, sign in, choose your tickets or table, and complete checkout.',
      },
      {
        id: 'tickets-find',
        title: 'Where can I find my tickets?',
        description: "Sign in and open My Tables and Tickets. You'll see everything you've purchased for this event.",
      },
      {
        id: 'tickets-refund',
        title: 'Can I get a refund on my tickets?',
        description:
          "Ticket refunds follow the organization's refund policy. Please contact the organization directly. Payment processing fees may not be refundable.",
      },
    ],
  },
  {
    id: 'silent-auction',
    title: 'Silent auction',
    items: [
      {
        id: 'silent-auction-bid',
        title: 'How do I bid?',
        description:
          'Open Silent Auction or All Items, find the item you want, and select Place Bid. Enter your bid and confirm. Each item shows its current bid and number of bids, and Filter helps you narrow items by category.',
      },
      {
        id: 'silent-auction-buy-now',
        title: 'What does "Buy Now" mean?',
        description:
          'Some items have a Buy Now price. Pay that price to win the item right away, without waiting for the auction to close.',
      },
      {
        id: 'silent-auction-outbid',
        title: "How will I know if I've been outbid?",
        description:
          "Check the item's current bid anytime on its listing. You can also opt in to receive an email or text if you're outbid.",
      },
      {
        id: 'silent-auction-close',
        title: 'When does bidding close?',
        description:
          "The auction start and end times are shown at the top of every auction page. Bids must be received before the close, so don't wait until the last second.",
      },
      {
        id: 'silent-auction-cancel',
        title: 'Can I cancel or change a bid?',
        description:
          'All bids are binding once placed. If you made an obvious mistake, such as an extra zero, contact the organization before the auction closes. They may remove the bid at their discretion.',
      },
      {
        id: 'silent-auction-tie',
        title: 'What happens if two people bid the same amount?',
        description: 'The bid that was received first wins.',
      },
      {
        id: 'silent-auction-won',
        title: 'How do I find out if I won?',
        description:
          'The highest bid at the close wins. The organization will notify winners, usually within seven days of the close.',
      },
      {
        id: 'silent-auction-pay',
        title: 'How do I pay for what I won?',
        description:
          "You pay the organization through the payment options on this site. Please pay promptly: if payment isn't received within 21 days of notification, the item may go to the next-highest bidder.",
      },
      {
        id: 'silent-auction-pickup',
        title: 'How do I pick up my item?',
        description:
          'The organization arranges pickup or delivery. Check the item description for details or contact the organization. Any shipping costs are set by the organization.',
      },
    ],
  },
  {
    id: 'live-auction',
    title: 'Live auction',
    items: [
      {
        id: 'live-auction-how',
        title: 'How does the live auction work?',
        description:
          "Live auction items are posted online so you can preview them before the event. Bidding happens in the room with the auctioneer during the event, so you can't place online bids on live items.",
      },
    ],
  },
  {
    id: 'raffle',
    title: 'Raffle',
    items: [
      {
        id: 'raffle-buy',
        title: 'How do I buy raffle tickets?',
        description:
          'Open Raffle, choose the number of tickets or a bundle, and select Buy Raffle Tickets. Raffle sales may close earlier than the auction, so check the "Sales End" time on the raffle page.',
      },
    ],
  },
  {
    id: 'donations',
    title: 'Donations',
    items: [
      {
        id: 'donations-make',
        title: 'How do I make a donation?',
        description:
          "Select Make Donation, sign in, enter your gift amount, and complete checkout. You don't need to attend the event to give.",
      },
      {
        id: 'donations-tax',
        title: 'Is my gift tax-deductible? Will I get a receipt?',
        description:
          'Your donation goes directly to the organization, and the organization issues tax acknowledgments. For tickets and auction items, usually only the amount above the fair market value is deductible. Keep your own records and check with your tax advisor.',
      },
      {
        id: 'donations-refund',
        title: 'Can I get a donation refunded?',
        description:
          'Donations are generally final. If you made a clear mistake, such as a duplicate gift, contact the organization promptly. If you set up a recurring gift, you can cancel future payments anytime using the link in your receipt.',
      },
    ],
  },
  {
    id: 'payments-and-security',
    title: 'Payments and security',
    items: [
      {
        id: 'payments-who',
        title: 'Who am I paying?',
        description:
          'Ticket purchases, auction wins, raffle tickets, and donations are paid to the organization hosting the event. Event Simpli provides the software.',
      },
      {
        id: 'payments-methods',
        title: 'What payment methods can I use?',
        description:
          'Payments are processed securely by Stripe, PayPal, or Square, depending on how the organization has set up its event. Major credit and debit cards are accepted.',
      },
      {
        id: 'payments-safe',
        title: 'Is my payment information safe?',
        description:
          "Yes. Event Simpli never stores your full card number. Card details are handled directly by the payment processor's PCI-compliant systems.",
      },
    ],
  },
  {
    id: 'still-need-help',
    title: 'Still need help?',
    items: [
      {
        id: 'help-contact',
        title: 'Who do I contact?',
        description:
          'For questions about this event, tickets, auction items, pickup, refunds, or donation receipts, contact the organization hosting the event. For trouble using the site, such as signing in or a page not loading, select Contact Us below, and our support team will help.',
      },
    ],
  },
];
